import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Meta } from '@angular/platform-browser';
import { NavigationError, Router, RouterOutlet } from '@angular/router';
import { isPublicAppRoute } from 'app/core/auth/post-logout-redirect.util';
import { initFlowbite } from 'flowbite';
import { filter } from 'rxjs';

@Component({
  selector: 'app-root',
  standalone: true,
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  private readonly meta = inject(Meta);
  private readonly router = inject(Router);

  constructor() {
    // Stale tab after deploy: lazy chunk fetch fails → full reload so the new build loads.
    // (SwUpdate is optional when using @angular/service-worker; this works without it.)
    this.router.events
      .pipe(
        filter((e): e is NavigationError => e instanceof NavigationError),
        takeUntilDestroyed()
      )
      .subscribe((event: NavigationError) => {
        const err = event.error as { message?: string } | undefined;
        if (err?.message?.includes('Failed to fetch dynamically imported module')) {
          window.location.reload();
        }
      });
  }

  public ngOnInit(): void {
    // Add no-cache headers to prevent sensitive pages from being cached
    this.meta.addTag({ 'http-equiv': 'Cache-Control', content: 'no-cache, no-store, must-revalidate' });
    this.meta.addTag({ 'http-equiv': 'Pragma', content: 'no-cache' });
    this.meta.addTag({ 'http-equiv': 'Expires', content: '0' });

    // Handle BFCache: back/forward can restore an authenticated shell without re-running guards.
    window.addEventListener('pageshow', (event: PageTransitionEvent) => {
      if (!event.persisted) {
        return;
      }

      const pathname = window.location.pathname;
      if (!isPublicAppRoute(pathname)) {
        window.location.replace(`${window.location.origin}/sign-out`);
        return;
      }

      const hasAccessToken = !!sessionStorage.getItem('accessToken');
      const hasIdToken = !!sessionStorage.getItem('idToken');
      if (!hasAccessToken || !hasIdToken) {
        window.location.reload();
      }
    });

    // Initialize Flowbite Javascript handlers
    initFlowbite();
  }
}
